import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { Vector3Tuple } from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import {
  useAnimation,
  useExpression,
  useAvatarQueue,
  useRandomEyeBlink,
} from '~/hooks/Avatar';
import { ExtendedObject3D } from '~/types';
import useChatAvatarStore from '~/store/useChatAvatarStore';
import useChatPageLoadingStatus from '~/store/useChatPageLoadingStatusStore';
import useToast from '~/store/useToast';

export const VisemePrefix = 'viseme_';
export const Lerp_Speed = 0.1;
export const EyeBlinkKey = ['eyeBlinkLeft', 'eyeBlinkRight'];

interface CharacterProps {
  id: string;
  visible?: boolean;
  avatarUrl: string;
  position: Vector3Tuple;
  scale?: Vector3Tuple;
}

export interface IdProps {
  id: CharacterProps['id'];
}

export default function RobotCharacter({
  id,
  avatarUrl,
  visible,
  position,
  scale = [8, 8, 8], // Default scale is [1, 1, 1]
}: CharacterProps) {
  const { showToast } = useToast();
  const { setChatPageLoadingEnd, isChatPageLoading } =
    useChatPageLoadingStatus();

  const characterRef = useRef<THREE.Mesh>(null!);

  const [scene, setScene] = useState<THREE.Group>();
  const [animations, setInitialAnimations] = useState<THREE.AnimationClip[]>(
    [],
  );

  useEffect(() => {
    const loader = new GLTFLoader();
    loader.load(
      avatarUrl,
      (gltf) => {
        setScene(gltf.scene);
        setInitialAnimations(gltf.animations);

        // 로딩 종료 이후 확실한 아바타 렌더 상태 보장을 위한 1초 추가 Waiting
        isChatPageLoading &&
          setTimeout(() => {
            setChatPageLoadingEnd();
          }, 1000);
      },
      undefined,
      (error) => {
        // Robot 아바타 모션 로딩 실패 => 토스트 띄우기
        console.error(`로봇 아바타 로드 실패:`, error);
        showToast('error', `Failed to Load Robot Avatar.`);
        isChatPageLoading && setTimeout(() => setChatPageLoadingEnd(), 500);
      },
    );
  }, [avatarUrl, id]);

  const { setInitialCharacterState, setMorph, resetMorph, setMorphKeys } =
    useChatAvatarStore((state) => ({
      setInitialCharacterState: state.setInitialCharacterState,
      setMorph: state.setMorphs,
      resetMorph: state.resetMorph,
      setMorphKeys: state.setMorphKeys,
    }));
  const { setAnimations } = useChatAvatarStore((state) => ({
    setAnimations: state.setAnimations,
  }));

  useEffect(() => {
    setInitialCharacterState({ id });
    resetMorph({ id });
    if (scene) {
      scene.traverse((x) => {
        if (
          // @ts-ignore
          x.morphTargetInfluences &&
          // @ts-ignore
          x.morphTargetInfluences.length &&
          // @ts-ignore
          x.morphTargetDictionary
        ) {
          // @ts-ignore
          Object.keys(x.morphTargetDictionary).forEach((el) =>
            setMorphKeys({
              id,
              keys: el,
            }),
          );

          setMorph({
            id,
            morphs: x as ExtendedObject3D,
          });
        }
      });
    }
  }, [scene, id]);

  useEffect(() => {
    if (animations.length > 0) {
      setAnimations({
        id,
        animations,
      });
    }
  }, [animations, id]);

  useRandomEyeBlink({ id });
  useExpression({ id });
  useAvatarQueue({ id });
  useAnimation({ scene, id });

  return (
    <>
      {scene && (
        <primitive
          key={id}
          visible={visible}
          ref={characterRef}
          object={scene}
          position={position}
          scale={scale} // Apply the scale here
        />
      )}
    </>
  );
}
